import React, { useRef, useEffect, useCallback } from 'react';

interface Dot {
    baseX: number;
    baseY: number;
    x: number;
    y: number;
    vx: number;
    vy: number;
}

interface InteractiveGridCanvasProps {
    orangeIntensity?: number; // 0..1, default 0.9
    orangeRadius?: number;    // default 220
    glowSoftness?: number;    // default 1.0
}

/**
 * Interactive background grid canvas with bounce effect and orange glow accents.
 * Dots push away from pointer and spring back.
 * Features:
 * - Cool base dots
 * - Orange glow overlay when near cursor
 * - Subtle orange heat gradient following cursor
 */
const InteractiveGridCanvas: React.FC<InteractiveGridCanvasProps> = ({
    orangeIntensity = 0.9,
    orangeRadius = 220,
    glowSoftness = 1.0
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const pointerRef = useRef({ x: -1000, y: -1000 });
    const dotsRef = useRef<Dot[]>([]);
    const rafRef = useRef<number>(0);
    const initializedRef = useRef(false);

    const initDots = useCallback((width: number, height: number) => {
        const gridSize = 40;
        const dots: Dot[] = [];

        for (let x = 0; x <= width; x += gridSize) {
            for (let y = 0; y <= height; y += gridSize) {
                dots.push({
                    baseX: x,
                    baseY: y,
                    x: x,
                    y: y,
                    vx: 0,
                    vy: 0
                });
            }
        }
        dotsRef.current = dots;
        initializedRef.current = true;
    }, []);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;

        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.scale(dpr, dpr);

        if (!initializedRef.current || dotsRef.current.length === 0) {
            initDots(width, height);
        }

        ctx.clearRect(0, 0, width, height);

        const pointerRadius = 120;
        const repelForce = 8;
        const springStiffness = 0.08;
        const dampening = 0.85;
        const { x: pointerX, y: pointerY } = pointerRef.current;

        // Orange "heat" gradient
        if (pointerX > 0 && pointerY > 0) {
            const heatRadius = orangeRadius * 1.5;
            const heatGradient = ctx.createRadialGradient(pointerX, pointerY, 0, pointerX, pointerY, heatRadius);
            heatGradient.addColorStop(0, `rgba(255, 140, 0, ${0.08 * orangeIntensity})`);
            heatGradient.addColorStop(0.5, `rgba(255, 160, 60, ${0.03 * orangeIntensity})`);
            heatGradient.addColorStop(1, 'rgba(255, 140, 0, 0)');

            ctx.save();
            ctx.globalCompositeOperation = 'lighter';
            ctx.fillStyle = heatGradient;
            ctx.fillRect(0, 0, width, height);
            ctx.restore();
        }

        // Draw grid lines - Sonic Style (Very subtle or hidden, dots are primary)
        ctx.strokeStyle = 'rgba(100, 116, 139, 0.0)'; // Hidden lines, focusing on dots like reference
        ctx.lineWidth = 0;
        const gridSize = 40;

        for (let x = 0; x <= width; x += gridSize) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, height);
            ctx.stroke();
        }
        for (let y = 0; y <= height; y += gridSize) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(width, y);
            ctx.stroke();
        }

        // Draw dots
        for (const dot of dotsRef.current) {
            const dx = dot.x - pointerX;
            const dy = dot.y - pointerY;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < pointerRadius && distance > 0) {
                const force = (1 - distance / pointerRadius) * repelForce;
                const angle = Math.atan2(dy, dx);
                dot.vx += Math.cos(angle) * force;
                dot.vy += Math.sin(angle) * force;
            }

            const springDx = dot.baseX - dot.x;
            const springDy = dot.baseY - dot.y;
            dot.vx += springDx * springStiffness;
            dot.vy += springDy * springStiffness;
            dot.vx *= dampening;
            dot.vy *= dampening;
            dot.x += dot.vx;
            dot.y += dot.vy;

            const displacement = Math.sqrt(
                Math.pow(dot.x - dot.baseX, 2) + Math.pow(dot.y - dot.baseY, 2)
            );

            const moveIntensity = Math.min(1, displacement / 30);
            const baseRadius = 2 + moveIntensity * 1.5;

            // Base dot visualization
            // Base dot visualization - Sonic Style (White/Light Orange)
            ctx.fillStyle = `rgba(255, 255, 255, ${0.3 + moveIntensity * 0.4})`;
            ctx.beginPath();
            ctx.arc(dot.x, dot.y, baseRadius, 0, Math.PI * 2);
            ctx.fill();

            // Orange glow accent
            if (distance < orangeRadius) {
                const normalizedDist = distance / orangeRadius;
                const orangeStrength = Math.pow(1 - normalizedDist, 2 * glowSoftness) * orangeIntensity;

                if (orangeStrength > 0.01) {
                    const glowRadius = baseRadius + 4 + (orangeStrength * 12);

                    const gradient = ctx.createRadialGradient(dot.x, dot.y, 0, dot.x, dot.y, glowRadius);
                    gradient.addColorStop(0, `rgba(255, 140, 0, ${orangeStrength * 0.95})`);
                    gradient.addColorStop(0.4, `rgba(255, 180, 80, ${orangeStrength * 0.4})`);
                    gradient.addColorStop(1, 'rgba(255, 140, 0, 0)');

                    ctx.save();
                    ctx.globalCompositeOperation = 'lighter';
                    ctx.fillStyle = gradient;
                    ctx.beginPath();
                    ctx.arc(dot.x, dot.y, glowRadius, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.restore();
                }
            }
        }

        rafRef.current = requestAnimationFrame(draw);
    }, [initDots, orangeIntensity, orangeRadius, glowSoftness]);

    // Handle resize & pointer events
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const handlePointerMove = (e: PointerEvent) => {
            const rect = canvas.getBoundingClientRect();
            pointerRef.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };
        };

        const handlePointerLeave = () => {
            pointerRef.current = { x: -1000, y: -1000 };
        };

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                if (width > 0 && height > 0) {
                    initDots(width, height);
                }
            }
        });

        canvas.addEventListener('pointermove', handlePointerMove);
        canvas.addEventListener('pointerleave', handlePointerLeave);
        resizeObserver.observe(canvas);

        rafRef.current = requestAnimationFrame(draw);

        return () => {
            canvas.removeEventListener('pointermove', handlePointerMove);
            canvas.removeEventListener('pointerleave', handlePointerLeave);
            resizeObserver.disconnect();
            cancelAnimationFrame(rafRef.current);
        };
    }, [draw, initDots]);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full pointer-events-auto opacity-40"
            style={{ width: '100%', height: '100%', transform: 'translate3d(0,0,0)', backfaceVisibility: 'hidden' }}
            aria-label="Interactive background grid"
            width={window.innerWidth}
            height={window.innerHeight}
        />
    );
};

export default InteractiveGridCanvas;
